import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useState, type MouseEvent } from 'react';

import { type PartnerEvent } from '@/api/routes/partners/partners.events.api';
import { IconDotsVertical } from '@/components/icon/tabler/IconDotsVertical';
import { IconEdit } from '@/components/icon/tabler/IconEdit';
import { IconEye } from '@/components/icon/tabler/IconEye';
import EventBannerThumb from '@/domain/events/components/EventBannerThumb';
import EventStatusChip from '@/domain/events/components/EventStatusChip';
import {
  EVENTS_PAGE_SIZE,
  eventCityLabel,
  eventRecurrenceShortLabel,
  formatEventWhen,
} from '@/domain/events/utils';
import { useDeviceMatch } from '@/hooks/useDeviceMatch';
import { useTranslate } from '@/state/i18n';

interface Props {
  events: PartnerEvent[];
  count: number;
  page: number;
  onPageChange: (page: number) => void;
  onOpen: (event: PartnerEvent) => void;
  onEdit: (event: PartnerEvent) => void;
}

/** P08 table on desktop, stacked rows on phones. Confirmations are a count, never names. */
const EventsTable = ({ events, count, page, onPageChange, onOpen, onEdit }: Props) => {
  const t = useTranslate('domain.events.components.EventsTable');
  const { isTablet } = useDeviceMatch();
  const [menu, setMenu] = useState<{ anchor: HTMLElement; event: PartnerEvent } | null>(null);

  const openMenu = (domEvent: MouseEvent<HTMLElement>, event: PartnerEvent) => {
    domEvent.stopPropagation();
    setMenu({ anchor: domEvent.currentTarget, event });
  };

  const nextDate = (event: PartnerEvent) =>
    event.nextOccurrence ? formatEventWhen(event.nextOccurrence, event.startTime) : t('ended');

  const actionsButton = (event: PartnerEvent) => (
    <IconButton
      size="small"
      aria-label={t('actions', { name: event.name })}
      aria-haspopup="menu"
      onClick={(domEvent) => openMenu(domEvent, event)}
    >
      <IconDotsVertical fontSize="small" />
    </IconButton>
  );

  return (
    <>
      {isTablet ? (
        <Stack divider={<Divider />}>
          {events.map((event) => (
            <Stack
              key={event.id}
              direction="row"
              gap={1.5}
              alignItems="flex-start"
              sx={sx.mobileRow}
              onClick={() => onOpen(event)}
            >
              <EventBannerThumb bannerUrl={event.bannerUrl} name={event.name} />
              <Stack flex={1} minWidth={0} gap={0.5}>
                <Typography variant="subtitle2" noWrap>
                  {event.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {[nextDate(event), eventCityLabel(event.addresses[0])]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
                <Stack direction="row" alignItems="center" gap={1}>
                  <EventStatusChip event={event} />
                  <Typography variant="caption" color="text.secondary">
                    {t('intents', { count: event.nextOccurrenceIntentsCount })}
                  </Typography>
                </Stack>
              </Stack>
              {actionsButton(event)}
            </Stack>
          ))}
        </Stack>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('columns.event')}</TableCell>
              <TableCell>{t('columns.next')}</TableCell>
              <TableCell>{t('columns.city')}</TableCell>
              <TableCell>{t('columns.status')}</TableCell>
              <TableCell align="right">{t('columns.intents')}</TableCell>
              <TableCell align="right" aria-label={t('columns.actions')} />
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((event) => {
              const recurrence = eventRecurrenceShortLabel(event);
              return (
                <TableRow key={event.id} hover sx={sx.row} onClick={() => onOpen(event)}>
                  <TableCell>
                    <Stack direction="row" alignItems="center" gap={1.5}>
                      <EventBannerThumb bannerUrl={event.bannerUrl} name={event.name} />
                      <Stack minWidth={0}>
                        <Typography variant="subtitle2" noWrap>
                          {event.name}
                        </Typography>
                        {event.eventType ? (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {event.eventType}
                          </Typography>
                        ) : null}
                      </Stack>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{nextDate(event)}</Typography>
                    {recurrence ? (
                      <Typography variant="caption" color="text.secondary">
                        {recurrence}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{eventCityLabel(event.addresses[0])}</Typography>
                  </TableCell>
                  <TableCell>
                    <EventStatusChip event={event} />
                  </TableCell>
                  <TableCell align="right">{event.nextOccurrenceIntentsCount}</TableCell>
                  <TableCell align="right">{actionsButton(event)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      {count > EVENTS_PAGE_SIZE ? (
        <TablePagination
          component="div"
          count={count}
          page={page}
          rowsPerPage={EVENTS_PAGE_SIZE}
          rowsPerPageOptions={[]}
          onPageChange={(_, nextPage) => onPageChange(nextPage)}
          labelDisplayedRows={({ from, to, count: total }) =>
            t('pagination', { from, to, count: total })
          }
          getItemAriaLabel={(type) => t(`paginationAria.${type}`)}
        />
      ) : null}
      <Menu
        anchorEl={menu?.anchor}
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            if (menu) onOpen(menu.event);
            setMenu(null);
          }}
        >
          <ListItemIcon>
            <IconEye fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('open')}</ListItemText>
        </MenuItem>
        {menu && !menu.event.canceledAt ? (
          <MenuItem
            onClick={() => {
              onEdit(menu.event);
              setMenu(null);
            }}
          >
            <ListItemIcon>
              <IconEdit fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('edit')}</ListItemText>
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
};

export default EventsTable;

const sx = {
  row: { cursor: 'pointer' },
  mobileRow: { p: 2, cursor: 'pointer' },
};
